<script setup lang="ts">
import { FormInputTable, FormValues } from "../types";
import { computed } from "vue";
import InputTable from "./input_table/InputTable.vue";
import { formValuesToJSONData, JSONDataToFormValues } from "../functions";
import { JSONData } from "@/data/base_classes/dto.d";
import { InputAttribute, TableValue } from "./input_table/types";
import { eventBus } from "../event_bus";

/////////////////////////////////////////////////////////////
//////////////////////// PROPS //////////////////////////////
/////////////////////////////////////////////////////////////
const props = defineProps<{
  config: FormInputTable;
  modelValue: FormValues[];
  formValues: FormValues;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: FormValues[]): void;
}>();

/////////////////////////////////////////////////////////////
////////////////////// VARIABLES ////////////////////////////
/////////////////////////////////////////////////////////////
const attributes = ref<InputAttribute[]>([]);
const subscriptionIds = ref<string[]>([]);
const loading = ref(false);

/////////////////////////////////////////////////////////////
////////////////////// COMPUTED /////////////////////////////
/////////////////////////////////////////////////////////////
const value = computed({
  get: () =>
    props.modelValue
      ? (props.modelValue.map(formValuesToJSONData) as unknown as TableValue[])
      : [],
  set: (value: TableValue[]) =>
    emit(
      "update:modelValue",
      value.map((v) => JSONDataToFormValues(v as unknown as JSONData)),
    ),
});

/////////////////////////////////////////////////////////////
////////////////////// METHODS //////////////////////////////
/////////////////////////////////////////////////////////////
function formatter(v: string | number): string {
  if (props.config.formatter) {
    return props.config.formatter(v);
  }
  return v.toString();
}

async function getAttributes(): Promise<InputAttribute[]> {
  if (typeof props.config.attributes === "function") {
    return await props.config.attributes(props.formValues);
  }
  return props.config.attributes;
}

async function setAttributes(): Promise<void> {
  loading.value = true;
  attributes.value = await getAttributes();
  setTimeout(() => {
    loading.value = false;
  }, 200);
}

async function setFormEventListeners(): Promise<void> {
  // Remove existing listeners
  removeFormEventListeners();

  // Add new listeners
  props.config.refreshOn?.forEach((event) => {
    const id = eventBus.subscribe(event, setAttributes);
    subscriptionIds.value.push(id);
  });
}

async function removeFormEventListeners(): Promise<void> {
  subscriptionIds.value.forEach((id) => {
    eventBus.unsubscribe(id);
  });
  subscriptionIds.value = [];
}

/////////////////////////////////////////////////////////////
////////////////////// LIFECYCLES ///////////////////////////
/////////////////////////////////////////////////////////////

const EVENT_NAME = "store.update.Attribute";
onMounted(async () => {
  setFormEventListeners();
  void setAttributes();

  // Listen for store updates
  window.removeEventListener(EVENT_NAME, setAttributes);
  window.addEventListener(EVENT_NAME, setAttributes);
});

onBeforeUnmount(() => {
  removeFormEventListeners();
  window.removeEventListener(EVENT_NAME, setAttributes);
});
</script>

<template>
  <InputTable
    v-if="!loading"
    v-model="value"
    :disabled="disabled"
    :formatter="formatter"
    :attributes="attributes"
  />
</template>
